import React, { useMemo } from 'react';
import { format, addDays, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';
import { IonIcon } from '@ionic/react';
import { timeOutline, locationOutline, chevronForward } from 'ionicons/icons';
import { useCalendarEvents } from '@/hooks/useCalendar';
import { useHistory } from 'react-router-dom';
import type { ServiceEvent } from '@/types/calendar';
import { RoleType, ROLE_LABELS, ROLE_ORDER } from '@/types/scheduling';
import type { RoleAssignment } from '@/types/scheduling';

const UpcomingEventsHero: React.FC = () => {
  const history = useHistory();
  const today = format(new Date(), 'yyyy-MM-dd');
  const futureLimit = format(addDays(new Date(), 45), 'yyyy-MM-dd');

  const { data: events = [], isLoading, isError } = useCalendarEvents({
    from: today,
    to: futureLimit,
    with_assignments: true,
  });

  const upcoming = events
    .filter((e) => !e.is_cancelled && e.event_date >= today)
    .sort((a, b) => {
      const d = a.event_date.localeCompare(b.event_date);
      return d !== 0 ? d : a.start_time.localeCompare(b.start_time);
    });

  const nextEvent = upcoming[0] ?? null;
  const followUp = upcoming.slice(1, 3);

  if (isLoading) {
    return (
      <div className="cal-hero-wrap">
        <div className="cal-hero cal-hero--skeleton">
          <div className="cal-skeleton-cell" style={{ height: 14, width: 140, marginBottom: 10 }} />
          <div className="cal-skeleton-cell" style={{ height: 22, width: '70%', marginBottom: 8 }} />
          <div className="cal-skeleton-cell" style={{ height: 14, width: '50%' }} />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="cal-hero-wrap">
        <div className="cal-hero cal-hero--empty">
          <span className="cal-hero__empty-text">Nu am putut încărca evenimentele.</span>
        </div>
      </div>
    );
  }

  if (!nextEvent) {
    return (
      <div className="cal-hero-wrap">
        <div className="cal-hero cal-hero--empty">
          <span className="cal-hero__empty-text">Nu sunt evenimente programate în perioada următoare.</span>
        </div>
      </div>
    );
  }

  const nextDate = parseISO(nextEvent.event_date);
  const nextDayStr = format(nextDate, 'EEEE, d MMMM', { locale: ro });

  const handleHeroClick = () => history.push(`/calendar/${nextEvent.id}`);
  const handleCalendarClick = () => history.push('/calendar');

  return (
    <div className="cal-hero-wrap">
      {/* Main hero card */}
      <div className="cal-hero" onClick={handleHeroClick}>
        <div className="cal-hero__orb" />
        <div className="cal-hero__content">
          <span className="cal-hero__overline">Următoarea întâlnire</span>
          <div className="cal-hero__title-row">
            <span className={`cal-hero__dot cal-hero__dot--${nextEvent.category}`} />
            <h3 className="cal-hero__title">{nextEvent.title}</h3>
          </div>
          <div className="cal-hero__meta">
            <span className="cal-hero__chip">
              <IonIcon icon={timeOutline} />
              <span style={{ textTransform: 'capitalize' }}>{nextDayStr}</span>, {nextEvent.start_time?.slice(0, 5)}
            </span>
            {nextEvent.location && (
              <span className="cal-hero__chip">
                <IonIcon icon={locationOutline} />
                {nextEvent.location}
              </span>
            )}
          </div>
          {nextEvent.assignments && nextEvent.assignments.length > 0 && (
            <EventLineup assignments={nextEvent.assignments} bibleReference={nextEvent.bible_reference} />
          )}
        </div>
      </div>

      {/* Follow-up events mini list */}
      {followUp.length > 0 && (
        <div className="cal-hero-list">
          {followUp.map((ev) => (
            <FollowUpItem key={ev.id} event={ev} onClick={() => history.push(`/calendar/${ev.id}`)} />
          ))}
          <button className="cal-hero-list__more" onClick={handleCalendarClick}>
            Calendar complet <IonIcon icon={chevronForward} />
          </button>
        </div>
      )}
    </div>
  );
};

const EventLineup: React.FC<{ assignments: RoleAssignment[]; bibleReference?: string | null }> = ({ assignments, bibleReference }) => {
  const grouped = useMemo(() => {
    const byRole = new Map<RoleType, RoleAssignment[]>();
    for (const a of assignments) {
      const role = a.role_type as RoleType;
      if (!byRole.has(role)) byRole.set(role, []);
      byRole.get(role)!.push(a);
    }
    return ROLE_ORDER
      .filter((r) => byRole.has(r))
      .map((r) => ({ role: r, items: byRole.get(r)! }));
  }, [assignments]);

  if (grouped.length === 0) return null;

  const formatName = (a: RoleAssignment) => {
    if (a.role_type === 'grup' && a.group) {
      return a.group.name.replace(/^Grupul\s*/i, 'Gr. ');
    }
    if (a.member) {
      const initial = a.member.first_name.charAt(0) + '.';
      return `${a.member.last_name} ${initial}`;
    }
    return null;
  };

  return (
    <div className="cal-hero-lineup">
      {bibleReference && (
        <div className="cal-hero-lineup__ref">{bibleReference}</div>
      )}
      {grouped.map(({ role, items }) => {
        const names = items.map(formatName).filter(Boolean);
        if (names.length === 0) return null;
        return (
          <div key={role} className="cal-hero-lineup__row">
            <span className={`cal-hero-lineup__label cal-hero-lineup__label--${role}`}>
              {ROLE_LABELS[role]}
            </span>
            <span className="cal-hero-lineup__names">{names.join(', ')}</span>
          </div>
        );
      })}
    </div>
  );
};

const FollowUpItem: React.FC<{ event: ServiceEvent; onClick: () => void }> = ({ event, onClick }) => {
  const date = parseISO(event.event_date);
  const dayNum = format(date, 'd');
  const monthShort = format(date, 'MMM', { locale: ro }).toUpperCase().replace('.', '');

  return (
    <div className="cal-hero-list__item" onClick={onClick}>
      <div className="cal-hero-list__date">
        <span className="cal-hero-list__date-num">{dayNum}</span>
        <span className="cal-hero-list__date-month">{monthShort}</span>
      </div>
      <div className={`cal-hero-list__bar cal-hero-list__bar--${event.category}`} />
      <div className="cal-hero-list__info">
        <span className="cal-hero-list__title">{event.title}</span>
        <span className="cal-hero-list__time">
          {event.start_time?.slice(0, 5)}
          {event.location ? ` · ${event.location}` : ''}
        </span>
      </div>
    </div>
  );
};

export default UpcomingEventsHero;
